import { MessageBarMessageItem, MessageBarResponse, MessageBarSeverity } from "@dining/api-client";
import { AppProps, StateSlice } from "./app";
import { MAX_ACTIVE_MESSAGES_FETCH_COUNT } from "../../../common/constants";

export type MessageSeverity = "info" | "warning" | "error" | "success";

export interface ISiteMessage {
  id: string;
  status: string;
  severity: MessageSeverity;
  markdown: string;
  startTimeUtc: string;
  endTimeUtc: string;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface SiteMessageResponse {
  scope: string;
  value: ISiteMessage[];
}

export type SiteMessageProps = {
  siteMessages: {
    values: ISiteMessage[];
    dismissedIds: string[];
    loading: boolean;
    error?: string;
  };
};

export type SiteMessageState = SiteMessageProps & {
  siteMessages: {
    fetch: () => Promise<void>;
    dismiss: (messageId: string) => void;
  };
};

export const defaultSiteMessageProps: SiteMessageProps = {
  siteMessages: {
    values: [],
    dismissedIds: [],
    loading: false,
    error: undefined,
  },
};

export const SITE_MESSAGE_CLIENT_SOURCE = "WebApp";

const mapSeverity = (severity?: MessageBarSeverity): MessageSeverity => {
  switch (severity) {
    case MessageBarSeverity.Warning:
      return "warning";
    case MessageBarSeverity.Error:
      return "error";
    case MessageBarSeverity.Success:
      return "success";
    case MessageBarSeverity.Info:
    default:
      return "info";
  }
};

const mapMessageToSiteMessage = (msg: MessageBarMessageItem): ISiteMessage => ({
  id: msg.id ?? "",
  status: msg.status?.toLowerCase() ?? "active",
  severity: mapSeverity(msg.severity),
  markdown: msg.messageText ?? "",
  startTimeUtc: msg.startTimeUtc ?? "",
  endTimeUtc: msg.endTimeUtc ?? "",
  createdAtUtc: msg.startTimeUtc ?? "",
  updatedAtUtc: msg.startTimeUtc ?? "",
});

export const createSiteMessageState: (props?: Partial<AppProps>) => StateSlice<SiteMessageState> =
  (props) => (set, get) => ({
    siteMessages: {
      ...defaultSiteMessageProps.siteMessages,
      ...props?.siteMessages,

      fetch: async () => {
        set(
          (state) => ({
            ...state,
            siteMessages: { ...state.siteMessages, loading: true, error: undefined },
          }),
          false,
          { type: "siteMessages.fetch.start" },
        );
        try {
          const { client } = get();
          if (!client) {
            throw new Error("Client is not defined");
          }

          const response: MessageBarResponse = await client.getMessages(
            SITE_MESSAGE_CLIENT_SOURCE,
            undefined,
            "active",
            undefined,
            undefined,
            MAX_ACTIVE_MESSAGES_FETCH_COUNT,
          );

          const messages: ISiteMessage[] = (response.messages ?? []).map(mapMessageToSiteMessage);

          set(
            (state) => ({
              ...state,
              siteMessages: { ...state.siteMessages, values: messages, loading: false },
            }),
            false,
            { type: "siteMessages.fetch.success", messages },
          );
        } catch (error) {
          get().appInsights.trackException(
            { exception: error as Error },
            { method: "siteMessages.fetch" },
          );
          set(
            (state) => ({
              ...state,
              siteMessages: {
                ...state.siteMessages,
                loading: false,
                error: "Failed to load site messages",
              },
            }),
            false,
            { type: "siteMessages.fetch.error", error },
          );
        }
      },

      dismiss: (messageId: string) => {
        set(
          (state) => ({
            ...state,
            siteMessages: {
              ...state.siteMessages,
              dismissedIds: state.siteMessages.dismissedIds.includes(messageId)
                ? state.siteMessages.dismissedIds
                : [...state.siteMessages.dismissedIds, messageId],
            },
          }),
          false,
          { type: "siteMessages.dismiss", messageId },
        );
      },
    },
  });
